import './donationLinkArchiveModal.scss';

import {
  DonationLink,
  useArchiveDonationLinkMutation,
} from '../../../types/__generated-graphQL__';
import React, { FunctionComponent, useContext, useEffect } from 'react';

import Alert from '@justgiving/alert';
import { AuthContext } from '@justgiving/auth-react';
import Loader from '@justgiving/loading';
import Modal from '@justgiving/modal';
import classNames from 'classnames';
import { createGraphQlClient } from '../../graphQl/baseApi';
import { useDonationLinkContext } from '../../context/donationLinkContext';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  donationLink: DonationLink | null;
};

const DonationLinkArchiveModal: FunctionComponent<Props> = ({
  isOpen,
  onRequestClose,
  donationLink,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { dispatch } = useDonationLinkContext();
  const auth = useContext(AuthContext);

  const archiveDonationLinkResult = useArchiveDonationLinkMutation(
    createGraphQlClient(auth),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['donationLinks.infinite'],
          exact: false,
        });
      },
    },
  );

  const { isError, isPending, isSuccess } = archiveDonationLinkResult;

  useEffect(() => {
    if (isSuccess) dispatch({ type: 'closeArchiveModal' });
  }, [isSuccess, dispatch]);

  if (!donationLink) return null;

  const modalClasses = classNames('donationLinkArchiveModal', {
    'donationLinkArchiveModal--isError': isError,
  });
  const { donationLinkId } = donationLink;

  return (
    <Modal
      {...{ isOpen, onRequestClose }}
      className={modalClasses}
      style={{
        overlay: { zIndex: 100 },
        content: {
          padding: 0,
          margin: 'auto',
          right: 'unset',
        },
      }}
    >
      <h2>{t('donationLinks.archive.title')}</h2>
      <p>{t('donationLinks.archive.subCopy')}</p>
      {isError && (
        <Alert
          type="error"
          displayType="inline"
          className="jg-space-mbsm jg-text--left"
        >
          {t('donationLinks.archive.error')}
        </Alert>
      )}
      <button
        className="jg-btn jg-space-mbsm donationLinkArchiveModal__button"
        onClick={() =>
          archiveDonationLinkResult.mutate({ input: { donationLinkId } })
        }
        disabled={isPending}
      >
        {isPending ? (
          <Loader
            colour="white"
            size="small"
            accessibleLabel={t('common.loading')}
          />
        ) : (
          t('donationLinks.archive.primaryButton')
        )}
      </button>
      <button
        className="jg-btn jg-btn--ghost donationLinkArchiveModal__button"
        onClick={() => dispatch({ type: 'closeArchiveModal' })}
      >
        {t('common.cancel')}
      </button>
    </Modal>
  );
};

export default DonationLinkArchiveModal;
